import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './YearPicker.module.scss';

type Props = {
  value: string;
  onChange: (year: string) => void;
  minYear?: number;
  maxYear?: number;
  allLabel?: string;
}

const YearPicker: FC<Props> = ({
  value,
  onChange,
  minYear = 2019,
  maxYear = new Date().getFullYear(),
  allLabel = 'Alla',
}) => {
  const [t] = useTranslation('shared', { keyPrefix: 'yearPicker' });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handlePrev = () => {
    if (!value) {
      onChange(String(minYear));
    } else {
      const prev = parseInt(value) - 1;
      if (prev >= minYear) onChange(String(prev));
    }
  };

  const handleNext = () => {
    if (!value) return;
    const next = parseInt(value) + 1;
    if (next <= maxYear) onChange(String(next));
    else onChange('');
  };

  const prevDisabled = !value || parseInt(value) <= minYear;
  const nextDisabled = !value || parseInt(value) >= maxYear;

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.control}>
        <button className={styles.arrow} onClick={handlePrev} disabled={prevDisabled}>
          &#8249;
        </button>
        <button className={styles.display} onClick={() => setOpen(o => !o)}>
          {value || allLabel}
        </button>
        <button className={styles.arrow} onClick={handleNext} disabled={nextDisabled}>
          &#8250;
        </button>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownLabel}>{t('filterByYear', 'Filtrera efter år')}</div>
          <div className={styles.grid}>
            {years.map(year => (
              <button
                key={year}
                className={`${styles.option} ${value === String(year) ? styles.active : ''}`}
                onClick={() => { onChange(String(year)); setOpen(false); }}
              >
                {year}
              </button>
            ))}
            <button
              className={`${styles.option} ${!value ? styles.active : ''}`}
              onClick={() => { onChange(''); setOpen(false); }}
            >
              {allLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YearPicker;
